import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Skull, ArrowLeft, Loader2, Gift as GiftIcon, Heart, Cake, Ghost, Snowflake, Mail, Info } from "lucide-react";
import { toast } from "sonner";

const DESIGN_ICONS = {
  halloween: Ghost,
  christmas: Snowflake,
  valentines: Heart,
  birthday: Cake,
  general: GiftIcon,
};

const Gifts = () => {
  const navigate = useNavigate();
  const [manifest, setManifest] = useState(null);
  const [kind, setKind] = useState("stored_value"); // 'stored_value' | 'gift_case'
  const [amountCents, setAmountCents] = useState(3500);
  const [customAmount, setCustomAmount] = useState("");
  const [caseId, setCaseId] = useState("");
  const [form, setForm] = useState({
    recipient_name: "",
    recipient_email: "",
    recipient_email_confirm: "",
    recipient_phone: "",
    delivery_method: "email",     // 'email' | 'sms' | 'both'
    sender_name: "",
    sender_email: "",
    message: "",
    design_id: "halloween",
    deliver_at: "", // '' means immediate
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/gifts/designs").then((r) => {
      setManifest(r.data);
      if (r.data?.cases?.length) setCaseId(r.data.cases[0].id);
    }).catch(() => toast.error("Could not load gift options"));
    api.post("/events", { event: "gift_page_viewed" }).catch(() => {});
  }, []);

  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const resolvedAmount = kind === "stored_value"
    ? (customAmount ? Math.round(parseFloat(customAmount) * 100) : amountCents)
    : null;

  const submit = async (e) => {
    e.preventDefault();
    const needsEmail = form.delivery_method === "email" || form.delivery_method === "both";
    const needsPhone = form.delivery_method === "sms" || form.delivery_method === "both";
    if (needsEmail && form.recipient_email.trim().toLowerCase() !== form.recipient_email_confirm.trim().toLowerCase()) {
      toast.error("Recipient email addresses do not match.");
      return;
    }
    if (needsPhone && !form.recipient_phone.trim()) {
      toast.error("Add the recipient's phone number for text delivery.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        kind,
        amount_cents: kind === "stored_value" ? resolvedAmount : undefined,
        case_id: kind === "gift_case" ? caseId : undefined,
        recipient_name: form.recipient_name,
        recipient_email: needsEmail ? form.recipient_email : undefined,
        recipient_email_confirm: needsEmail ? form.recipient_email_confirm : undefined,
        recipient_phone: needsPhone ? form.recipient_phone : "",
        delivery_method: form.delivery_method,
        sender_name: form.sender_name,
        sender_email: form.sender_email,
        message: form.message,
        design_id: form.design_id,
        deliver_at: form.deliver_at || "now",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/gifts/checkout", payload);
      api.post("/events", { event: "gift_purchase_initiated", props: { kind, method: form.delivery_method } }).catch(() => {});
      window.location.href = data.checkout_url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not create your gift. Try again.");
      setLoading(false);
    }
  };

  if (!manifest) {
    return (
      <div className="grain min-h-screen flex items-center justify-center text-[#a39797]">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  const minDate = new Date().toISOString().split("T")[0];
  const maxDate = new Date(Date.now() + manifest.max_schedule_days * 86400000).toISOString().split("T")[0];

  return (
    <div className="grain min-h-screen" data-testid="gifts-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#a39797] hover:text-[#d4af37] transition-colors mb-10" data-testid="gifts-back">
          <ArrowLeft size={16} /> back to the file
        </Link>

        <div className="font-hand text-[#d4af37] rotate-[-1deg] text-2xl mb-2">give a mystery</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-3">A gift that leaves a body.</h1>
        <p className="text-[#a39797] mb-8 max-w-2xl">
          Send a wasntme.party gift. The recipient picks the night, the case, and the suspects. No expiration.
        </p>

        <form onSubmit={submit} className="space-y-7">
          {/* Kind picker */}
          <div className="grid md:grid-cols-2 gap-3">
            <button type="button" onClick={() => setKind("stored_value")}
              data-testid="pick-stored-value"
              className={`card-noir p-5 text-left transition-colors ${kind === "stored_value" ? "border-[#8b0000] bg-[#8b0000]/10" : "hover:border-[#8b0000]"}`}>
              <div className="font-display text-xl mb-1">Gift card</div>
              <div className="text-xs text-[#a39797]">A $ amount they spend on anything.</div>
            </button>
            <button type="button" onClick={() => setKind("gift_case")}
              data-testid="pick-gift-case"
              className={`card-noir p-5 text-left transition-colors ${kind === "gift_case" ? "border-[#8b0000] bg-[#8b0000]/10" : "hover:border-[#8b0000]"}`}>
              <div className="font-display text-xl mb-1">Gift a case</div>
              <div className="text-xs text-[#a39797]">Pick one case. We hide the price from them.</div>
            </button>
          </div>

          {/* Amount or case */}
          {kind === "stored_value" ? (
            <div>
              <div className="text-xs tracking-widest text-[#a39797] mb-2">AMOUNT</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                {manifest.preset_amounts_cents.map((cents) => (
                  <button key={cents} type="button"
                    onClick={() => { setAmountCents(cents); setCustomAmount(""); }}
                    data-testid={`amount-${cents}`}
                    className={`card-noir py-3 transition-colors ${!customAmount && amountCents === cents ? "border-[#8b0000] bg-[#8b0000]/10" : "hover:border-[#8b0000]"}`}>
                    <div className="font-display text-xl">${cents / 100}</div>
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[#a39797] text-sm">Or any amount</span>
                <input type="number"
                  min={manifest.min_custom_cents / 100} max={manifest.max_custom_cents / 100} step="1"
                  className="input-noir w-36" placeholder={`${manifest.min_custom_cents / 100}–${manifest.max_custom_cents / 100}`}
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  data-testid="input-custom-amount" />
              </div>
            </div>
          ) : (
            <div>
              <div className="text-xs tracking-widest text-[#a39797] mb-2">PICK A CASE</div>
              <select className="input-noir" value={caseId} onChange={(e) => setCaseId(e.target.value)}
                data-testid="select-gift-case">
                {manifest.cases.map((c) => (
                  <option key={c.id} value={c.id}>Case #{c.number} · {c.title}</option>
                ))}
              </select>
              <div className="text-xs text-[#a39797] mt-2 flex items-start gap-2">
                <Info size={12} className="mt-0.5" />
                If they already own this case, we'll convert the gift to balance they can spend on anything.
              </div>
            </div>
          )}

          {/* Design picker */}
          <div>
            <div className="text-xs tracking-widest text-[#a39797] mb-2">CARD DESIGN</div>
            <div className="grid grid-cols-5 gap-2">
              {manifest.designs.map((d) => {
                const Icon = DESIGN_ICONS[d.id] || GiftIcon;
                const active = form.design_id === d.id;
                return (
                  <button key={d.id} type="button" onClick={() => u("design_id", d.id)}
                    data-testid={`design-${d.id}`}
                    className={`card-noir p-3 text-center transition-colors ${active ? "border-[#8b0000] bg-[#8b0000]/10" : "hover:border-[#8b0000]"}`}
                    style={{ borderColor: active ? d.accent_hex : undefined }}>
                    <Icon size={20} className="mx-auto mb-1" style={{ color: d.accent_hex }} />
                    <div className="text-xs">{d.label}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Recipient */}
          <div className="space-y-3 pt-2 border-t border-[#2d2424]">
            <div className="text-xs tracking-widest text-[#a39797]">RECIPIENT</div>
            <input className="input-noir" placeholder="Their name" required
              value={form.recipient_name} onChange={(e) => u("recipient_name", e.target.value)}
              data-testid="input-recipient-name" />

            <div className="text-xs tracking-widest text-[#a39797] mt-3">HOW SHOULD WE SEND IT?</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: "email", label: "Email" },
                { id: "sms", label: "Text" },
                { id: "both", label: "Both" },
              ].map((opt) => {
                const active = form.delivery_method === opt.id;
                return (
                  <button key={opt.id} type="button" onClick={() => u("delivery_method", opt.id)}
                    data-testid={`delivery-${opt.id}`}
                    className={`card-noir py-2 transition-colors ${active ? "border-[#8b0000] bg-[#8b0000]/10" : "hover:border-[#8b0000]"}`}>
                    <div className="text-sm">{opt.label}</div>
                  </button>
                );
              })}
            </div>

            {(form.delivery_method === "email" || form.delivery_method === "both") && (
              <div className="grid md:grid-cols-2 gap-3">
                <input type="email" className="input-noir" placeholder="Their email" required
                  value={form.recipient_email} onChange={(e) => u("recipient_email", e.target.value)}
                  data-testid="input-recipient-email" />
                <input type="email" className="input-noir" placeholder="Confirm their email" required
                  value={form.recipient_email_confirm}
                  onChange={(e) => u("recipient_email_confirm", e.target.value)}
                  data-testid="input-recipient-email-confirm" />
              </div>
            )}
            {(form.delivery_method === "sms" || form.delivery_method === "both") && (
              <div>
                <input type="tel" className="input-noir"
                  placeholder="Their phone (e.g. +14155551234)"
                  value={form.recipient_phone}
                  onChange={(e) => u("recipient_phone", e.target.value)}
                  data-testid="input-recipient-phone" required />
                <div className="text-xs text-[#a39797] mt-1">
                  Include the country code. We'll text the code and a redeem link.
                </div>
              </div>
            )}
          </div>

          {/* Sender */}
          <div className="space-y-3 pt-2 border-t border-[#2d2424]">
            <div className="text-xs tracking-widest text-[#a39797]">FROM YOU</div>
            <div className="grid md:grid-cols-2 gap-3">
              <input className="input-noir" placeholder="Your name" required
                value={form.sender_name} onChange={(e) => u("sender_name", e.target.value)}
                data-testid="input-sender-name" />
              <input type="email" className="input-noir" placeholder="Your email (for receipt)" required
                value={form.sender_email} onChange={(e) => u("sender_email", e.target.value)}
                data-testid="input-sender-email" />
            </div>
            <textarea className="input-noir min-h-[80px]" placeholder="A short message (optional, max 250 chars)"
              maxLength={250} value={form.message} onChange={(e) => u("message", e.target.value)}
              data-testid="input-gift-message" />
            <div className="text-xs text-[#a39797] text-right">{form.message.length}/250</div>
          </div>

          {/* Delivery */}
          <div className="space-y-3 pt-2 border-t border-[#2d2424]">
            <div className="text-xs tracking-widest text-[#a39797]">WHEN TO DELIVER</div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={!form.deliver_at} onChange={() => u("deliver_at", "")}
                  className="accent-[#8b0000]" data-testid="deliver-now" />
                <span className="text-sm">Deliver now</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={!!form.deliver_at} onChange={() => u("deliver_at", minDate)}
                  className="accent-[#8b0000]" data-testid="deliver-schedule" />
                <span className="text-sm">Schedule</span>
              </label>
            </div>
            {!!form.deliver_at && (
              <input type="date" className="input-noir" min={minDate} max={maxDate}
                value={form.deliver_at} onChange={(e) => u("deliver_at", e.target.value)}
                data-testid="input-deliver-date" />
            )}
            <div className="text-xs text-[#a39797]">
              Scheduled gifts arrive at 9:00 am in your time zone. You can fix the email or date up to a few hours after you buy.
            </div>
          </div>

          <div className="pt-4 border-t border-[#2d2424] flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs text-[#a39797] tracking-widest">YOU PAY</div>
              <div className="font-display text-3xl text-[#f4f0ec]" data-testid="gift-total">
                {kind === "stored_value" && resolvedAmount ? `$${(resolvedAmount / 100).toFixed(2)}` : (kind === "gift_case" ? "price of the case" : "—")}
              </div>
              <div className="text-xs text-[#a39797] mt-1">
                Promo codes don't apply when buying a gift. The recipient can stack their gift later.
              </div>
            </div>
            <button type="submit" disabled={loading} className="btn-primary disabled:opacity-50"
              data-testid="submit-gift">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <GiftIcon size={18} />}
              {loading ? "Preparing…" : "Buy the gift"}
            </button>
          </div>

          <div className="text-center pt-6">
            <Link to="/gifts/balance" className="text-xs underline text-[#a39797]" data-testid="gifts-balance-link">
              <Mail size={12} className="inline mr-1" /> Check a gift card balance
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Gifts;
